import type { FilterClause } from '../api/types';

/** One-line operator-readable summary of a config's filter clause. Recurses to
 * any depth so nested groups are shown faithfully (never silently flattened). */
export function summarizeFilter(filter: FilterClause | null): string {
  if (filter === null) return 'matches every product';
  if (filter.kind === 'single') return `${filter.field} ${filter.op} "${filter.value}"`;
  if (filter.clauses.length === 0) return 'matches every product';
  const joiner = filter.kind === 'or' ? ' OR ' : ' AND ';
  return filter.clauses
    .map((c) => (c.kind === 'single' ? summarizeFilter(c) : `(${summarizeFilter(c)})`))
    .join(joiner);
}

/**
 * True iff the flat visual FilterEditor can represent `filter` LOSSLESSLY.
 *
 * The editor's model is a flat list of rows joined by AND, where each row is
 * either a single clause or an OR-group of single clauses. It cannot represent
 * deeper nesting (an OR-group containing an AND-group — "OR of ANDs" — or any
 * depth-3 structure). Editing such a filter in the flat editor would drop the
 * clauses it can't model (FE-A-1), silently re-targeting the config; when this
 * returns false the editor must render the filter read-only instead.
 */
export function isFlatEditable(filter: FilterClause | null): boolean {
  if (filter === null || filter.kind === 'single') return true;
  if (filter.kind === 'or') return filter.clauses.every((c) => c.kind === 'single');
  // and-group: every row is a single clause or an OR-group of single clauses.
  return filter.clauses.every(
    (c) => c.kind === 'single' || (c.kind === 'or' && c.clauses.every((d) => d.kind === 'single')),
  );
}
